import { DELI_ENV_BASE_URL, http_v3 } from '@api/client';
import * as designationService from '@api/person/designation';
import { AppNoBodyPadding } from '@components/app-settings';
import { useFormSnackbarError } from '@lib/common';
import { CompanyDetail } from '@lib/company-mapping';
import { AppBar, FormControl, Grid, InputLabel, Select, TextField, Tooltip, Typography } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { purple } from '@material-ui/core/colors';
import { Autocomplete } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import { ControlledTextField } from 'pages/person-management/person-management-form/person-master-relation-form';
import { MarketsResponsibleField } from '../c2p-features/markets-responsible-field';
import React, { useEffect, useMemo, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { map } from 'rxjs/operators';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { SingleBuyerSellerCell } from '../columns/utils';
import { C2PType, Relation } from '../types';
import { FormActions } from './form-actions';
import * as adapter from './form-adapter';

export const NOT_SURE = "not-sure";
type C2PFormProps = {
  onSubmit: (company: C2PType) => any;
  onDelete?: (company: C2PType) => any;
  c2pData: C2PType;
  createCompany?: boolean;
  redirectToSearch : () => void;
};

export function C2PForm({
  c2pData,
  onSubmit,
  onDelete = null,
  createCompany = false,
  redirectToSearch = ()=>{}
}: C2PFormProps) {
  const c2pFormData = useMemo(
    () => adapter.toForm(c2pData),
    [c2pData]
  );

  const form = useForm({
    defaultValues: c2pFormData,
    // resolver: vestResolver(dealValidationSuite),
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    form.watch("currently_working");
    form.watch("designation")
    form.watch("markets_responsible")
    form.watch("description")
    form.watch('source_links')
    form.register("person_vtr_id")
    form.register("company_vtr_id")
    form.register("person_company_relation_id");
    form.register("markets_responsible");
  }, [])

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const formError = useFormSnackbarError();

  const submitForm = form.handleSubmit(onSubmit, formError);
  useEffect(() => {
    if (form.errors && form.errors['company2_duplicate']) {
      const company2 = form.watch('company2');
      if (company2) {
        const company2_names = (company2 as unknown as CompanyDetail[]).map(x => x.name);
        const duplicate_company2s = company2_names.filter(
          (x, idx) => company2_names.indexOf(x) != idx
        );
        enqueueSnackbar(`${duplicate_company2s.join(', ')} in Company-2's are duplicated`, {
          variant: 'warning',
        });
      }
    }
  }, [form.errors]);

  const CompanyMappingAppBar = () => {
    return (
      <AppBar color="secondary" style={{ position: 'sticky', top: 64, background: purple[800] }}>
        <Grid
          container
          justify="flex-end"
          alignItems="flex-end"
          spacing={2}
          style={{ margin: 0, width: '100%' }}>
          <Grid item>
            <FormActions
              submitForm={submitForm}
              redirectToSearch={redirectToSearch}
              c2pFormData={c2pFormData}
            />
          </Grid>
        </Grid>
      </AppBar>
    );
  };

  const imdb_id = c2pData?.person?.imdb_id;
  const hubspot_id = c2pData?.person?.hubspot_id;
  const linkedin_url = c2pData?.person?.linkedin_url;
  const person_id = c2pData?.person?.person_id;
  const person_master = c2pData?.person?.vtr_id;

  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // Debounced function to fetch role options
  const debouncedSearchRoles = useCallback(
    debounce((searchTerm: string) => {
      setLoading(true);
      http_v3
        .get('/roles', {
          params: {
            role_name: searchTerm
          }
        })
        .pipe(
          map(res => res.data)
        )
        .subscribe({
          next: data => {
            let rolesArray = [];
            if (Array.isArray(data)) {
              rolesArray = data;
            } else if (data && Array.isArray(data.data)) {
              rolesArray = data.data;
            } else if (data && typeof data === 'object') {
              // If it's an object, try to extract array from common properties
              rolesArray = data.roles || data.items || data.results || [];
            }
            setOptions(rolesArray);
          },
          error: err => {
            console.error(err, 'Error fetching roles');
            setOptions([]); // Set empty array on error
          },
          complete: () => {
            setLoading(false);
          },
        });
    }, 300),
    []
  );

  useEffect(() => {
    // Initial load - fetch all roles
    debouncedSearchRoles('');
  }, [debouncedSearchRoles]);
  return (
    <>
      <AppNoBodyPadding />
      <div>
        <FormProvider {...form}>
          <form onSubmit={submitForm}>

            <CompanyMappingAppBar />
            <FormDiv>
              <Grid container xs={10} spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="h5" gutterBottom>
                    Name
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {c2pData?.person?.name ? c2pData?.person?.name : ''}
                  </Typography>
                </Grid>
                <br />
                <br />
                <Grid item xs={4}>
                  <CellForCompEntity companyName={c2pData?.company_name} relationInfo={c2pData?.relation} />
                </Grid>
                <br />
                <br />

              </Grid>
              <br />
              <br />
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography variant="h6" gutterBottom>
                    Designation
                  </Typography>
                 
                      <Autocomplete
                        options={options || []}
                        ref={form.register("designation") as any}
                        value={form.getValues("designation")}
                        onChange={(e,v)=>{
                          form.setValue('designation',v)
                        }}
                        freeSolo
                        onInputChange={(e,v)=>{
                          if (e?.type === 'change') { 
                            form.setValue('designation',v);
                            debouncedSearchRoles(v);
                          }
                        }}
                        renderInput={props => (
                          <TextField
                            label="Role"
                            fullWidth
                            variant="outlined"
                            {...props}
                            InputProps={{
                              ...props.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {loading ? (
                                    <>
                                      <CircularProgress color="inherit" size={20} />
                                      {props.InputProps.endAdornment}
                                    </>
                                  ) : null}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="h6" gutterBottom style={{ whiteSpace: 'nowrap' }}>
                    Currently Working
                  </Typography>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="currently_working">Currently Working</InputLabel>
                    <Select
                      labelId="currently_working"
                      id="currently_working"
                      name="currently_working"
                      inputRef={form.register}
                      native
                      label="Currently Working"
                      defaultValue={c2pFormData?.currently_working ?? ''}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                      <option value={NOT_SURE}>Not Sure</option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Markets Responsible
                  </Typography>
                  <MarketsResponsibleField
                    value={form.watch('markets_responsible') || ''}
                    onChange={value => form.setValue('markets_responsible', value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Description
                  </Typography>
                  <ControlledTextField
                    fullWidth
                    variant="outlined"
                    name={'description'}
                    label={'Description'}
                    control={form.control}
                    defaultValue={c2pFormData?.description ?? ''}
                    multiline
                    rows={5}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Sources Link
                  </Typography>
                  <ControlledTextField
                    fullWidth
                    variant="outlined"
                    name={'source_links'}
                    label={'Sources Link'}
                    control={form.control}
                    defaultValue={c2pFormData?.source_links ?? ''}
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
              <br />
              <br />
              <Grid style={{ position: 'relative' }} container spacing={3}>
                {person_id && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    TMDB
                  </Typography>
                  <Tooltip title="TMDB">
                    <a target="_blank" style={{ display: "flex" }} href={`https://www.themoviedb.org/person/${person_id}`}>
                      <img style={{ height: "30px", width: "30px", marginRight: "20px", borderRadius: "4px", overflow: "hidden",   }} src="/images/tmdb.jpeg" />
                    </a>
                  </Tooltip>
                </Grid>)}
                {linkedin_url && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    LinkedIn URL
                  </Typography>
                  <Tooltip title="Linkedin">
                    <a target="_blank" style={{ display: "flex" }} href={`${linkedin_url}`}>
                      <img style={{ height: "30px", width: "30px", marginRight: "20px", borderRadius: "4px", overflow: "hidden",   }} src="/images/linkedin.png" />
                    </a>
                  </Tooltip>
                </Grid>)}
                {hubspot_id && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    Hubspot Id
                  </Typography>
                  <Tooltip title="Hubspot">
                    <a target="_blank" style={{ display: "flex" }} href={`https://app.hubspot.com/contacts/20258191/record/0-1/${hubspot_id}`}>
                      <img style={{ height: "30px", width: "30px", marginRight: "20px", borderRadius: "4px", overflow: "hidden",   }} src="/images/HS_logo.jpeg" />
                    </a>
                  </Tooltip>
                </Grid>)}
                {imdb_id && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    IMDB
                  </Typography>
                  <Tooltip title="IMDB">
                    <a target="_blank" style={{ display: "flex" }} href={`https://www.imdb.com/name/${imdb_id}`}>
                      <img style={{ height: "30px", width: "30px", marginRight: "20px", borderRadius: "4px", overflow: "hidden",   }} src="/images/imdb.png" />
                    </a>
                  </Tooltip>
                </Grid>)}
                {person_master && (<Grid item xs={2}>
                  <Typography variant="h6" gutterBottom>
                    Person Master
                  </Typography>

                  <Tooltip title="Person Master">
                    <a target="_blank" style={{ display: "flex" }} href={`${DELI_ENV_BASE_URL}/person-management/edit/${person_master}`}>
                      <img style={{ height: "30px", width: "30px", borderRadius: "4px", overflow: "hidden",   }} src="/images/vitrina.png" />
                    </a>
                  </Tooltip>

                </Grid>)}
              </Grid>
            </FormDiv>
          </form>
        </FormProvider>
      </div>
    </>
  );
}

export const CellForCompEntity = ({ companyName, relationInfo }: { companyName: string, relationInfo: Relation }) => {
  const { company_vtr_id = "" }: { company_vtr_id: string } = relationInfo;
  const RenderComponent = SingleBuyerSellerCell;

  if (!company_vtr_id) {
    return null;
  }
  const firstEntityName = companyName;
  return (
    <>
      <RenderComponent
        name={<>{firstEntityName} </>}
        vtrId={company_vtr_id}
      />
    </>
  );
};

const FormDiv = styled.div`
  padding: 30px 10%;
`;


